import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import http from 'core/services/http';
import Config from 'core/config';
import { useEventId } from './useEventId';

const SCRIPT_ID = 'act-chatbot-widget';
const WIDGET_ID = 'act-chat-widget';
const INIT_STYLE_ID = 'act-chatbot-init-style';

function injectScript(widgetUrl, apiUrl) {
    if (document.getElementById(SCRIPT_ID)) return;

    // Hide the widget during init to suppress the open→close flash
    const style = document.createElement('style');
    style.id = INIT_STYLE_ID;
    style.textContent = `#${WIDGET_ID} { display: none !important; }`;
    document.head.appendChild(style);

    const configScript = document.createElement('script');
    configScript.textContent = "window.ACTChatConfig = { role: 'tcw' };";
    document.body.appendChild(configScript);

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = widgetUrl;
    script.setAttribute('data-api', apiUrl);
    script.onload = () => {
        // Wait for widget to finish its init cycle then open it
        setTimeout(() => {
            document.getElementById(WIDGET_ID)?.querySelector('button')?.click();
            document.getElementById(INIT_STYLE_ID)?.remove();
        }, 500);
    };
    document.body.appendChild(script);
}

function setWidgetVisible(visible) {
    const el = document.getElementById(WIDGET_ID);
    if (el) el.style.display = visible ? '' : 'none';
}

function removeChatbot() {
    document.getElementById(SCRIPT_ID)?.remove();
    document.getElementById(WIDGET_ID)?.remove();
    document.getElementById(INIT_STYLE_ID)?.remove();
}

export function ChatbotWidget() {
    const userId = useSelector(state => state.users.currentUser?.id);
    const eventId = useEventId();
    useLocation(); // re-evaluate on every navigation
    const cacheRef = useRef({ eventId: null });
    const prevUserIdRef = useRef(userId);

    // Remove widget entirely on logout or user switch so the third-party
    // widget session does not bleed across users on the same tab.
    // React runs effects in declaration order, so cacheRef is reset before
    // the chatbot config effect below re-evaluates.
    useEffect(() => {
        if (prevUserIdRef.current !== userId) {
            removeChatbot();
            cacheRef.current = { eventId: null };
        }
        prevUserIdRef.current = userId;
    }, [userId]);

    useEffect(() => {
        if (!eventId || !userId) {
            setWidgetVisible(false);
            return;
        }
        if (eventId === cacheRef.current.eventId) {
            setWidgetVisible(true);
            return;
        }

        let cancelled = false;

        http.get(`${Config.apiUrl}/testcenterevent/${eventId}/chatbot/config`)
            .then((data) => {
                if (cancelled) return;
                if (data.enabled) {
                    cacheRef.current = { eventId };
                    injectScript(data.widgetUrl, data.apiUrl);
                    setWidgetVisible(true);
                } else {
                    setWidgetVisible(false);
                }
            })
            .catch(() => { /* chatbot config unavailable — degrade silently */ });

        return () => { cancelled = true; };
    }, [eventId, userId]); // userId ensures re-evaluation after user switch on same event page

    return null;
}
